import { useEffect, useState } from 'react'

import cn from 'classnames'
import { toast } from 'react-hot-toast'
import Skeleton from 'react-loading-skeleton'
import { useLocation } from 'react-router-dom'

import { ChainType } from '@lifi/types'
import { getConnectorIcon } from '@lifi/wallet-management'
import { useWallet as useTronWallet } from '@tronweb3/tronwallet-adapter-react-hooks'

import ChainToken from '@src/components/ChainToken'
import CopyToClipboard from '@src/components/CopyToClipboard'
import PendingTransactions from '@src/components/PendingTransactions'
import LogoutIcon from '@src/components/icons/Logout'
import NoWalletIcon from '@src/components/icons/NoWallet'
import { ChainId, getChainNameFromChainId } from '@src/configs/chainInfo'
import * as PATH from '@src/configs/paths'
import { MAX_TOKEN_DISPLAY_DECIMALS } from '@src/constants/exchange'
import { useFetchAllBalances } from '@src/hooks/useFetchAllBalances'
import { useAuthContext } from '@src/providers/AuthProvider'
import { IToken } from '@src/types/token'
import { Balance } from '@src/utils/balance'
import { bigintPow } from '@src/utils/bigintPow'
import { formatNumber } from '@src/utils/formatFee'
import { createContextLogger } from '@src/utils/logger'
import { truncateAddress } from '@src/utils/truncateAddress'
import { useApplicationStore, useTokensStore } from '@src/zustand_stores'

interface GroupedToken {
    symbol: string
    name: string
    tokens: IToken[]
    totalBalanceUSD: number
    logoURI: string
}

// Define chain filter type for portfolio drawer
type ChainFilter = 'all' | 'evm' | 'tron' | 'solana'

const logger = createContextLogger('WalletDrawer')

const WalletPortfolioDrawer = ({
    open,
    onClose,
}: {
    open: boolean
    onClose: () => void
    onTokenSelect?: (token: IToken) => void
}) => {
    const [isLoading, setIsLoading] = useState(true)
    const { accounts, connectHandler, disconnectHandler } = useAuthContext()
    // Add state for chain filter
    const [chainFilter, setChainFilter] = useState<ChainFilter>('all')
    // Get Tron wallet information
    const {
        connected: isTronConnected,
        address: tronAddress,
        disconnect: disconnectTronWallet,
        wallet: tronWallet,
    } = useTronWallet()

    /**
     * * * * * * * * * * * * * * * * * * * * *  NEW ZUSTAND STORES * * * * * * * * * * * * * * *
     */
    const generatedReferralCode = useApplicationStore(state => state.generatedReferralCode)

    const { tokens: zustandTokens, isLoading: zustandTokensLoading } = useTokensStore()
    const { isLoading: balanceResponsesLoading } = useFetchAllBalances()

    useEffect(() => {
        setIsLoading(
            zustandTokensLoading ||
                balanceResponsesLoading ||
                (!zustandTokensLoading && !balanceResponsesLoading && zustandTokens.length === 0)
        )
    }, [zustandTokensLoading, balanceResponsesLoading, zustandTokens])
    /**
     * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
     */

    const [internalOpen, setInternalOpen] = useState<boolean>(false)
    useEffect(() => {
        setInternalOpen(open)
    }, [open])

    const [totalBalance, setTotalBalance] = useState(0.1)
    const [totalBalanceEVM, setTotalBalanceEVM] = useState(0.1)
    const [totalBalanceTron, setTotalBalanceTron] = useState(0.1)
    const [totalBalanceSolana, setTotalBalanceSolana] = useState(0.1)

    useEffect(() => {
        setTotalBalance(totalBalanceEVM + totalBalanceTron + totalBalanceSolana)
    }, [totalBalanceEVM, totalBalanceTron, totalBalanceSolana])

    useEffect(() => {
        if (!accounts || accounts.length === 0) {
            // close out the portfolio drawer
            setInternalOpen(false)
            setTimeout(() => onClose(), 300)
        }
    }, [accounts])

    const preFilter = (token: IToken) => {
        try {
            const tokenBalanceThreshold = bigintPow(
                BigInt(10),
                BigInt(Math.max(0, token.decimals - MAX_TOKEN_DISPLAY_DECIMALS))
            )
            return (
                token.userBalance !== undefined &&
                token.userBalance > tokenBalanceThreshold &&
                token.userBalanceInUSD &&
                token.userBalanceInUSD > 0
            )
        } catch (e) {
            logger.error(`Cannot filter token:`, token)
            return false
        }
    }

    const sortTokens = (tokens: IToken[]): GroupedToken[] => {
        // Group tokens by symbol
        const groupedBySymbol = tokens.reduce<Record<string, GroupedToken>>((acc, token) => {
            if (!token.symbol) return acc

            const key = token.symbol
            if (!acc[key]) {
                acc[key] = {
                    symbol: token.symbol,
                    name: token.name || token.symbol,
                    tokens: [],
                    totalBalanceUSD: 0,
                    logoURI: token.logoURI || '',
                }
            }
            acc[key].tokens.push(token)
            acc[key].totalBalanceUSD += token.userBalanceInUSD || 0
            return acc
        }, {})

        // Convert to array and sort by USD value
        const groupedTokens = Object.values(groupedBySymbol)
        return groupedTokens.sort((a, b) => b.totalBalanceUSD - a.totalBalanceUSD)
    }

    const [filteredEVMTokens, setFilteredEVMTokens] = useState<IToken[]>([])
    const [filteredTronTokens, setFilteredTronTokens] = useState<IToken[]>([])
    const [filteredSolanaTokens, setFilteredSolanaTokens] = useState<IToken[]>([])

    useEffect(() => {
        if (zustandTokens.length > 0) {
            const filteredTokens = zustandTokens.filter(preFilter)

            const _filteredEVMTokens = filteredTokens.filter(
                t => t.chainId !== ChainId.Solana && t.chainId !== ChainId.Tron
            )
            const sumBalanceEVM = _filteredEVMTokens.reduce((p, c) => p + (c.userBalanceInUSD || 0), 0)
            setTotalBalanceEVM(sumBalanceEVM)

            const _filteredTronTokens = filteredTokens.filter(t => t.chainId === ChainId.Tron)
            const sumBalanceTron = _filteredTronTokens.reduce((p, c) => p + (c.userBalanceInUSD || 0), 0)
            setTotalBalanceTron(sumBalanceTron)

            const _filteredSolanaTokens = filteredTokens.filter(t => t.chainId === ChainId.Solana)
            const sumBalanceSolana = _filteredSolanaTokens.reduce((p, c) => p + (c.userBalanceInUSD || 0), 0)
            setTotalBalanceSolana(sumBalanceSolana)

            setFilteredEVMTokens(_filteredEVMTokens)
            setFilteredTronTokens(_filteredTronTokens)
            setFilteredSolanaTokens(_filteredSolanaTokens)
        }
    }, [zustandTokens])

    const WalletBalanceListItem: React.FC<{ groupedToken: GroupedToken }> = ({ groupedToken }) => {
        const setWalletSelectedSellToken = useApplicationStore(state => state.setWalletSelectedSellToken)
        const location = useLocation()
        const [isExpanded, setIsExpanded] = useState(false)

        // Sort tokens within group by USD value
        const sortedTokens = [...groupedToken.tokens].sort(
            (a, b) => (b.userBalanceInUSD || 0) - (a.userBalanceInUSD || 0)
        )

        const totalBalance = sortedTokens.reduce((total, token) => {
            if (!token.userBalance) return total
            const balance = new Balance(token)
                .setValue(token.userBalance)
                .formatWithMagnitudes(MAX_TOKEN_DISPLAY_DECIMALS)

            // Remove commas before parsing
            const cleanBalance = balance.replace(/,/g, '')
            return total + (parseFloat(cleanBalance) || 0)
        }, 0)

        const handleTokenSelect = (token: IToken) => {
            logger.info(`Selected token:`, token)
            if (location.pathname === PATH.SWAP) {
                setWalletSelectedSellToken(token)
            }
            setInternalOpen(false)
            setTimeout(() => onClose(), 300)
        }

        return (
            <>
                <div
                    className='flex w-full cursor-pointer items-center space-x-3 px-4 py-2.5 transition-all duration-300 hover:bg-[#22242A]'
                    onClick={() => {
                        if (sortedTokens.length > 1) {
                            setIsExpanded(!isExpanded)
                        } else if (sortedTokens[0]) {
                            handleTokenSelect(sortedTokens[0])
                        }
                    }}
                >
                    <div className='relative'>
                        <ChainToken tokenLogo={groupedToken.logoURI} />
                        <div className='absolute -bottom-1.5 -right-1.5 flex -space-x-2'>
                            {sortedTokens.slice(0, 3).map((token, index) => (
                                <img
                                    key={`${token.chainId}-${token.address}-${index}`}
                                    src={`/assets/network/${getChainNameFromChainId(token.chainId)}.png`}
                                    className='h-5 w-5 rounded-full border-2 border-modal-background'
                                    alt={getChainNameFromChainId(token.chainId)}
                                />
                            ))}
                        </div>
                    </div>

                    <div className='flex-1 text-sm'>
                        <p className='font-semibold'>{groupedToken.name}</p>
                        <p className='font-medium text-gray-400'>
                            {totalBalance.toFixed(6)}&nbsp;{groupedToken.symbol}
                        </p>
                    </div>

                    <div className='flex items-center space-x-2'>
                        {sortedTokens.length > 1 && (
                            <svg
                                className={cn('h-4 w-4 transition-transform', isExpanded ? 'rotate-180' : '')}
                                viewBox='0 0 24 24'
                                fill='none'
                                xmlns='http://www.w3.org/2000/svg'
                            >
                                <path
                                    d='M6 9L12 15L18 9'
                                    stroke='currentColor'
                                    strokeWidth='2'
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                />
                            </svg>
                        )}
                        <p className='text-sm font-bold'>${formatNumber(groupedToken.totalBalanceUSD)}</p>
                    </div>
                </div>

                {/* Dropdown for individual network tokens */}
                <div
                    className={cn(
                        'overflow-hidden transition-all duration-300',
                        isExpanded ? 'max-h-[500px]' : 'max-h-0'
                    )}
                >
                    {sortedTokens.map((token, tokenIndex) => (
                        <div
                            key={`${token.chainId}-${token.address}-${tokenIndex}`}
                            className='flex w-full cursor-pointer items-center space-x-3 bg-[#1D1F23] px-6 py-2 text-[13px] transition-all duration-300 hover:bg-[#22242A]'
                            onClick={() => handleTokenSelect(token)}
                        >
                            <div className='relative'>
                                <ChainToken
                                    tokenLogo={token.logoURI}
                                    networkIcon={`/assets/network/${getChainNameFromChainId(token.chainId)}.png`}
                                    chainIconSize={18}
                                    tokenIconSize={32}
                                />
                            </div>

                            <div className='flex-1'>
                                <p className='font-semibold'>{token.name}</p>
                                <p className='font-medium text-gray-400'>
                                    {token.userBalance !== undefined
                                        ? new Balance(token)
                                              .setValue(token.userBalance)
                                              .formatWithMagnitudes(MAX_TOKEN_DISPLAY_DECIMALS)
                                        : '0'}
                                    &nbsp;{token.symbol}
                                </p>
                            </div>

                            <p className='font-bold'>${formatNumber(token.userBalanceInUSD || 0)}</p>
                        </div>
                    ))}
                </div>
            </>
        )
    }

    const renderAllBalances = () => {
        // Filter tokens based on selected chain
        let filteredTokens: IToken[] = []

        switch (chainFilter) {
            case 'evm':
                filteredTokens = filteredEVMTokens
                break
            case 'tron':
                filteredTokens = filteredTronTokens
                break
            case 'solana':
                filteredTokens = filteredSolanaTokens
                break
            case 'all':
            default:
                filteredTokens = [...filteredEVMTokens, ...filteredTronTokens, ...filteredSolanaTokens]
        }

        if (filteredTokens.length === 0) {
            return <div className='py-4 text-center text-gray-400'>No tokens found for the selected chain</div>
        }

        // Sort all tokens together
        const groupedTokens = sortTokens(filteredTokens)
        return groupedTokens.map((group, idx) => (
            <WalletBalanceListItem key={`${group.symbol}-${idx}`} groupedToken={group} />
        ))
    }

    // Get current balance based on chain filter
    const getCurrentBalance = () => {
        switch (chainFilter) {
            case 'evm':
                return totalBalanceEVM
            case 'tron':
                return totalBalanceTron
            case 'solana':
                return totalBalanceSolana
            case 'all':
            default:
                return totalBalance
        }
    }

    // Get current chain name for display
    const getChainFilterName = () => {
        switch (chainFilter) {
            case 'evm':
                return 'EVM Networks'
            case 'tron':
                return 'Tron'
            case 'solana':
                return 'Solana'
            case 'all':
            default:
                return 'All Networks'
        }
    }

    const renderLoader = (count: number = 5) => {
        return Array(count)
            .fill(0)
            .map((_, id) => (
                <div key={`skeleton-${id}`} className='flex w-full items-center space-x-3 px-4 py-2'>
                    <Skeleton enableAnimation baseColor='#9B9B9B' circle className='h-9 w-9' />
                    <div>
                        <Skeleton enableAnimation baseColor='#9B9B9B' className='w-20' />
                        <Skeleton enableAnimation baseColor='#9B9B9B' className='w-20' />
                    </div>
                    <Skeleton enableAnimation baseColor='#9B9B9B' className='w-20' containerClassName='!ml-auto' />
                </div>
            ))
    }

    // Chain filter tab button component
    const ChainFilterTab = ({ value, label, icon }: { value: ChainFilter; label: string; icon?: string }) => (
        <button
            onClick={() => setChainFilter(value)}
            className={cn(
                'flex items-center justify-center space-x-1.5 rounded-md px-4 py-2 text-sm font-medium transition-colors',
                chainFilter === value
                    ? 'bg-[#22242A] text-white'
                    : 'text-gray-400 hover:bg-[#1D1F23] hover:text-gray-300'
            )}
        >
            {icon && <img src={icon} alt={label} className={cn('max-h-5 min-h-5 min-w-5 max-w-5 rounded-full')} />}
            <span>{label}</span>
        </button>
    )

    return (
        <div
            className={cn(
                'fixed bottom-0 right-0 z-[100] h-[calc(100%-60px)] overflow-hidden md:h-full',
                open ? 'opacity-100' : 'pointer-events-none opacity-0'
            )}
        >
            <div
                className='fixed bottom-0 left-0 right-0 top-0 z-10 cursor-pointer bg-black/50 backdrop-blur-sm'
                onClick={() => {
                    setInternalOpen(false)
                    setTimeout(() => onClose(), 300)
                }}
            ></div>
            <div
                className={cn(
                    'relative z-20 h-full w-[420px] max-w-[420px] space-y-3 overflow-auto bg-[#141618] transition-all duration-300',
                    internalOpen ? 'translate-0' : 'translate-y-full md:translate-x-full md:translate-y-0'
                )}
                style={{
                    boxShadow: 'rgba(0, 0, 0, 0.35) 0px 5px 15px',
                }}
            >
                <div className='space-y-4 pt-5'>
                    <div className='flex items-center justify-between px-4'>
                        <h2 className='text-2xl font-semibold'>Portfolio</h2>
                        <button
                            onClick={connectHandler}
                            className='rounded-full bg-[#2A2E37] px-4 py-2 text-sm font-medium text-white hover:bg-[#363B45]'
                        >
                            Connect Another Wallet
                        </button>
                    </div>

                    {/* Wallets list section */}
                    <div className='px-4'>
                        <h3 className='mb-2 text-sm font-medium text-gray-400'>
                            {(accounts && accounts.length > 0) || isTronConnected
                                ? `${(accounts?.length || 0) + (isTronConnected ? 1 : 0)} Connected ${(accounts?.length || 0) + (isTronConnected ? 1 : 0) === 1 ? 'Wallet' : 'Wallets'}`
                                : 'Connected Wallets'}
                        </h3>
                        <div className='space-y-3'>
                            {(accounts && accounts.length > 0) || isTronConnected ? (
                                <>
                                    {accounts &&
                                        accounts.map((account, index) => {
                                            return (
                                                <div
                                                    key={`${account.address}-${index}`}
                                                    className='flex items-center justify-between'
                                                >
                                                    <div
                                                        className='group flex cursor-pointer items-center gap-3'
                                                        onClick={() => {
                                                            if (account.address) {
                                                                navigator.clipboard.writeText(account.address)
                                                                toast.success('Address copied!', {
                                                                    duration: 2000,
                                                                    position: 'top-right',
                                                                    style: {
                                                                        background: '#16a34a',
                                                                        color: '#fff',
                                                                        fontWeight: 500,
                                                                        fontSize: '14px',
                                                                        padding: '12px 16px',
                                                                        borderRadius: '6px',
                                                                    },
                                                                })
                                                            }
                                                        }}
                                                    >
                                                        <div className='flex items-center gap-3'>
                                                            <div
                                                                style={{
                                                                    margin: 'auto',
                                                                    height: 40,
                                                                    width: 40,
                                                                    backgroundColor: 'white',
                                                                    borderRadius: '12px',
                                                                    position: 'relative',
                                                                }}
                                                            >
                                                                {account.connector && (
                                                                    <img
                                                                        src={getConnectorIcon(account.connector)}
                                                                        alt='Wallet'
                                                                        style={{
                                                                            height: '100%',
                                                                            width: '100%',
                                                                            objectFit: 'contain',
                                                                        }}
                                                                    />
                                                                )}

                                                                {/* Chain Icon */}
                                                                <div className='absolute -bottom-1.5 -right-1.5'>
                                                                    <img
                                                                        src={
                                                                            account.chainType === ChainType.SVM
                                                                                ? '/assets/network/solana.png'
                                                                                : account.chainId === ChainId.Tron
                                                                                  ? '/assets/network/tron.png'
                                                                                  : (account as any).chain?.logoURI ||
                                                                                    '/assets/network/ethereum.png'
                                                                        }
                                                                        alt={`chain-${account.chainType === ChainType.SVM ? 'solana' : account.chainId === ChainId.Tron ? 'tron' : 'evm'}`}
                                                                        className={cn(
                                                                            'h-5 w-5 rounded-full border-2 border-[#1A1C24]',
                                                                            (account as any).chain?.logoURI?.endsWith(
                                                                                'unichain.svg'
                                                                            )
                                                                                ? 'bg-white'
                                                                                : ''
                                                                        )}
                                                                        onError={e => {
                                                                            e.currentTarget.src =
                                                                                '/assets/token/unknown.png'
                                                                        }}
                                                                    />
                                                                </div>
                                                            </div>
                                                            <p className='rounded-md px-3 py-1.5 text-lg font-semibold text-gray-200 transition-all duration-300 group-hover:bg-[#22242A] group-hover:text-white'>
                                                                {truncateAddress(account.address || '', {
                                                                    evmPrefixLen: 5,
                                                                    evmSuffixLen: 5,
                                                                    solanaPrefixLen: 7,
                                                                    solanaSuffixLen: 5,
                                                                    tronPrefixLen: 5,
                                                                    tronSuffixLen: 5,
                                                                })}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <button
                                                        onClick={() => disconnectHandler(account)}
                                                        className='flex items-center gap-2 rounded-md bg-[#22242A] px-3 py-1.5 text-sm font-medium text-gray-300 transition-colors hover:bg-[#2A2D33]'
                                                    >
                                                        <LogoutIcon className='h-4 w-4' />
                                                        Disconnect
                                                    </button>
                                                </div>
                                            )
                                        })}

                                    {/* Tron Wallet */}
                                    {isTronConnected && tronAddress && (
                                        <div key={`tron-wallet`} className='flex items-center justify-between'>
                                            <div
                                                className='group flex cursor-pointer items-center gap-3'
                                                onClick={() => {
                                                    if (tronAddress) {
                                                        navigator.clipboard.writeText(tronAddress)
                                                        toast.success('Address copied!', {
                                                            duration: 2000,
                                                            position: 'top-right',
                                                            style: {
                                                                background: '#16a34a',
                                                                color: '#fff',
                                                                fontWeight: 500,
                                                                fontSize: '14px',
                                                                padding: '12px 16px',
                                                                borderRadius: '6px',
                                                            },
                                                        })
                                                    }
                                                }}
                                            >
                                                <div className='flex items-center gap-3'>
                                                    <div
                                                        style={{
                                                            margin: 'auto',
                                                            height: 40,
                                                            width: 40,
                                                            position: 'relative',
                                                        }}
                                                    >
                                                        <img
                                                            src={tronWallet?.adapter.icon || '/assets/network/tron.png'}
                                                            alt='Tron Wallet'
                                                            style={{
                                                                height: '100%',
                                                                width: '100%',
                                                                objectFit: 'contain',
                                                                borderRadius: '12px',
                                                                overflow: 'hidden',
                                                            }}
                                                        />

                                                        {/* Chain Icon */}
                                                        <div className='absolute -bottom-1.5 -right-1.5'>
                                                            <img
                                                                src={'/assets/network/tron.png'}
                                                                alt='tron-chain'
                                                                className='h-5 w-5 rounded-full border-2 border-[#1A1C24]'
                                                                onError={e => {
                                                                    e.currentTarget.src = '/assets/token/unknown.png'
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                    <p className='rounded-md px-3 py-1.5 text-lg font-semibold text-gray-200 transition-all duration-300 group-hover:bg-[#22242A] group-hover:text-white'>
                                                        {truncateAddress(tronAddress, {
                                                            evmPrefixLen: 5,
                                                            evmSuffixLen: 5,
                                                            solanaPrefixLen: 7,
                                                            solanaSuffixLen: 5,
                                                            tronPrefixLen: 5,
                                                            tronSuffixLen: 5,
                                                        })}
                                                    </p>
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => disconnectTronWallet()}
                                                className='flex items-center gap-2 rounded-md bg-[#22242A] px-3 py-1.5 text-sm font-medium text-gray-300 transition-colors hover:bg-[#2A2D33]'
                                            >
                                                <LogoutIcon className='h-4 w-4' />
                                                Disconnect
                                            </button>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className='py-4 text-center text-gray-400'>No wallets connected</div>
                            )}
                        </div>
                    </div>

                    <div className='my-3 border-t border-gray-800'></div>

                    {/* Pending Transactions Section */}
                    <PendingTransactions onClose={onClose} />

                    {accounts &&
                        accounts.length > 0 &&
                        generatedReferralCode.code !== '' &&
                        accounts.some(
                            account =>
                                account.address &&
                                account.address.toLowerCase() === generatedReferralCode.userAddress.toLowerCase()
                        ) && (
                            <div className='flex items-center px-4'>
                                <p className='text-[13px] text-gray-400'>
                                    Referral Code:{' '}
                                    <span className='ml-1 font-medium text-[#85C3F8]'>
                                        {generatedReferralCode.code}
                                    </span>
                                </p>
                                <div className='ml-2'>
                                    <CopyToClipboard
                                        textToCopy={generatedReferralCode.code}
                                        textType='Referral Code'
                                        anchorId='referral-code'
                                    />
                                </div>
                            </div>
                        )}

                    <h3 className='px-4 text-5xl font-bold'>
                        {isLoading ? (
                            <Skeleton enableAnimation baseColor='#9B9B9B' className='h-12 w-24' />
                        ) : (
                            <>${Number(formatNumber(getCurrentBalance())).toFixed(2)}</>
                        )}
                    </h3>
                </div>

                <div className='!mt-4'>
                    {isLoading ? (
                        renderLoader()
                    ) : (
                        <>
                            <div className='mb-4 px-4'>
                                <p className='mb-2'>
                                    {getChainFilterName()}
                                    <span className='text-sm text-gray-400'>
                                        &nbsp;(${Number(formatNumber(getCurrentBalance())).toFixed(2)})
                                    </span>
                                </p>

                                {/* Chain filter tabs */}
                                <div className='flex flex-wrap gap-2'>
                                    <ChainFilterTab value='all' label='All' />
                                    {/* Sort chain tabs by USD value from most to least */}
                                    {[
                                        {
                                            value: 'evm' as ChainFilter,
                                            label: 'EVM',
                                            icon:
                                                (accounts?.[0] as any)?.chain?.logoURI ||
                                                '/assets/network/ethereum.png',
                                            balance: totalBalanceEVM,
                                        },
                                        {
                                            value: 'solana' as ChainFilter,
                                            label: 'Solana',
                                            icon: '/assets/network/solana.png',
                                            balance: totalBalanceSolana,
                                        },
                                        {
                                            value: 'tron' as ChainFilter,
                                            label: 'Tron',
                                            icon: '/assets/network/tron.png',
                                            balance: totalBalanceTron,
                                        },
                                    ]
                                        .sort((a, b) => b.balance - a.balance) // Sort by balance (high to low)
                                        .map(chain => (
                                            <ChainFilterTab
                                                key={chain.value}
                                                value={chain.value}
                                                label={chain.label}
                                                icon={chain.icon}
                                            />
                                        ))}
                                </div>
                            </div>
                            {renderAllBalances()}
                        </>
                    )}
                </div>
                {isLoading == false && getCurrentBalance() <= 0 && (
                    <div className='margin-auto'>
                        <NoWalletIcon style={{ margin: 'auto' }} />
                        <div className='space-y-3 text-center'>
                            <p className='text-xl font-semibold'>No balances found</p>
                            <span className='text-xs font-medium'>Sure you&apos;ve got the right wallet?</span>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}

export default WalletPortfolioDrawer
